// Sign Brief page - displays basic or standard terms agreement for creator to review and sign

import {
  buildProposedDeliverableGuidelines,
  SignBriefView,
  type EditBriefFormData,
} from "@/components/terms/sign-brief-view";
import { captureEvent } from "@/lib/analytics-loader";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import { useChannelListQuery } from "@/lib/queries/channel";
import { useSessionQuery } from "@/lib/queries/session";
import {
  useAgreeToBasicTermsMutation,
  useAgreeToStandardTermsMutation,
  useBasicTermsQuery,
  useProposeStandardBriefChangesMutation,
  useStandardBriefEnhancementsQuery,
  type BasicTermsDeliverable,
} from "@/lib/queries/terms";
import {
  formatRelativeDate,
  toBackendDateString,
} from "@/lib/utils/date-format";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import type { CampaignRouteContext } from "@/lib/types/route-context";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/sign-brief/$interactionId",
)({
  component: SignBriefPage,
});

function SignBriefPage() {
  const { interactionId } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { campaign, programId, profile, organization } = context;
  const navigate = useNavigate();
  const { data: sessionData } = useSessionQuery();

  // Get interactions for this project to find the secureUrl
  const {
    data: interactionsData,
    isPending: interactionsPending,
    error: interactionsError,
  } = useProjectInteractionsQuery(organization.id, programId, profile.id);

  // Find the specific interaction by ID
  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );

  const secureUrl = interaction?.secureUrl ?? "";

  // Get basic terms using the secureUrl from the interaction
  const {
    data: termsData,
    isPending: termsPending,
    error: termsError,
  } = useBasicTermsQuery(organization.id, secureUrl);

  const terms = termsData?.terms;

  // Fetch brief enhancements (custom terms, content agreement, etc.)
  // Enable 5s polling while a Paid Partnership Ads authorization is pending
  // so the pre-sign gate flips to "completed" as soon as the creator
  // approves on their Instagram app — matches the legacy UCE cadence.
  // Use `isPending` (not `isFetching`) so the skeleton only shows on the
  // initial load; background polls must not flash the page to a skeleton.
  const { data: enhancementsData, isPending: enhancementsPending } =
    useStandardBriefEnhancementsQuery(organization.id, secureUrl, {
      pollWhilePpaPending: true,
    });

  const enhancements = enhancementsData?.enhancements;

  // Check if the creator has a connected YouTube channel using our own DB.
  // Aspirex doesn't yet return requireYoutubeAuth/youtubeAccountsAuthorized, so
  // we derive the connection status locally — same source the Social Accounts
  // page uses. This drives the connect prompt and success state in the brief.
  const { data: channels } = useChannelListQuery(organization.id, profile.id);
  const youtubeChannel = channels?.find((c) => c.platform === "youtube");
  const youtubeChannelConnected = !!youtubeChannel;
  const youtubeChannelName =
    youtubeChannel?.handle ?? youtubeChannel?.displayName ?? null;
  // Deep-link to the creator's specific channel in YouTube Studio so the
  // "Open YouTube Studio" button in the 3P consent dialog goes to the right place.
  const youtubeStudioUrl = youtubeChannel
    ? `https://studio.youtube.com/channel/${youtubeChannel.platformAccountId}?d=ycdsd`
    : "https://studio.youtube.com/?d=ycdsd";

  // The canceled field is included in the basic terms response
  const isCanceled = terms?.canceled === true;

  const agreeToBasicMutation = useAgreeToBasicTermsMutation(organization.id);
  const agreeToStandardMutation = useAgreeToStandardTermsMutation(
    organization.id,
  );
  const proposeChangesMutation = useProposeStandardBriefChangesMutation(
    organization.id,
  );

  const handleProposeChanges = async (data: EditBriefFormData) => {
    if (!secureUrl || !enhancements || enhancements.memberId == null) {
      toast.error("Unable to propose changes right now. Please try again.");
      return;
    }

    try {
      await proposeChangesMutation.mutateAsync({
        secureUrl,
        clientId: enhancements.clientId,
        memberId: enhancements.memberId,
        price: data.payment,
        offersPayment: data.payment != null && data.payment > 0,
        products: data.deliverables
          .filter((d) => !d.removed)
          .map((d) => {
            return {
              postType: d.postType ?? d.type,
              count: d.count,
              deliverableName: d.deliverableName ?? undefined,
              requiresApproval: d.requiresApproval,
              deliverableGuidelines: buildProposedDeliverableGuidelines(
                d.categorizedGuidelines,
                d.postType ?? d.type,
                d.attachments,
              ),
              dueDates: d.dueDates?.map(
                (dd: BasicTermsDeliverable["dueDates"][number]) => ({
                  completion: dd.completion
                    ? {
                        start_date:
                          toBackendDateString(dd.completion.start_date) ??
                          undefined,
                        end_date:
                          toBackendDateString(dd.completion.end_date) ??
                          undefined,
                        type: dd.completion.type,
                        description: dd.completion.description,
                        due_date_in_days: dd.completion.due_date_in_days,
                      }
                    : null,
                  submission: dd.submission
                    ? {
                        start_date:
                          toBackendDateString(dd.submission.start_date) ??
                          undefined,
                        end_date:
                          toBackendDateString(dd.submission.end_date) ??
                          undefined,
                        type: dd.submission.type,
                        description: dd.submission.description,
                        due_date_in_days: dd.submission.due_date_in_days,
                      }
                    : null,
                }),
              ),
            };
          }),
        contentRightsLanguage: data.contentUsageRights,
        usageRightsDuration: data.usageRightsDuration,
        requireExclusivity: data.requireExclusivity ?? undefined,
        requireAdvertiserAccess: data.requireAdvertiserAccess ?? undefined,
        accessStartDate: data.accessStartDate,
        accessEndDate: data.accessEndDate,
        requireTiktokSparkAdsAccess:
          data.requireTiktokSparkAdsAccess ?? undefined,
        tiktokSparkAdsDuration: data.tiktokSparkAdsDuration,
        requireInstagramInsights: data.requireInstagramInsights ?? undefined,
        isCreatorEdit: true,
        userEmail: profile.email,
        offersCommission: !!terms?.commission,
        offersProduct: !!terms?.freeProduct,
        commissionDescription: terms?.commission ?? undefined,
        productDescription: terms?.freeProduct ?? undefined,
        briefUrl: enhancements.briefUrl ?? undefined,
        requireContentRights: enhancements.requireContentRights ?? undefined,
        exclusivityLanguage: enhancements.exclusivityLanguage ?? undefined,
        textMessage: enhancements.textMessage ?? undefined,
        advertiserSocialAccountIds:
          enhancements.advertiserSocialAccountIds ?? undefined,
        creatorContentAgreement:
          enhancements.creatorContentAgreement ?? undefined,
      });

      captureEvent("brief_changes_proposed", {
        organizationId: organization.id,
        interactionId,
      });

      toast.success(
        "Your proposed changes have been sent to the brand for review.",
      );

      // Navigate back to project page so the UI refreshes with updated stage
      navigate({
        to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
        params: {
          orgSlug: organization.slug,
          profileSlug: campaign.profileSlug,
          campaignId: String(programId),
        },
      });
    } catch (error) {
      toast.error("Failed to submit proposed changes. Please try again.");
      throw error;
    }
  };

  const handleTermsAgreed = () => {
    captureEvent("terms_agreed", {
      organizationId: organization.id,
      interactionId,
    });
    navigate({
      to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
      params: {
        orgSlug: organization.slug,
        profileSlug: campaign.profileSlug,
        campaignId: String(programId),
      },
    });
  };

  const handleAcceptBasicTerms = async ({
    agreementId,
    memberName,
  }: {
    agreementId: string;
    memberName: string;
  }) => {
    await agreeToBasicMutation.mutateAsync({
      agreementId,
      memberName,
    });
  };

  const handleAcceptStandardTerms = async ({
    secureUrl,
    agreementId,
    memberName,
    signature,
  }: {
    secureUrl: string;
    agreementId: string;
    memberName: string;
    signature: string;
  }) => {
    await agreeToStandardMutation.mutateAsync({
      secureUrl,
      agreementId,
      memberName,
      signature,
      workItemId: interactionId,
    });
  };

  const updatedLabel = formatRelativeDate(interaction?.updatedDate);

  return (
    <div className="flex flex-col">
      <SignBriefView
        isLoading={interactionsPending || (!!secureUrl && termsPending)}
        error={interactionsError}
        interactionFound={!!interaction}
        secureUrlFound={!!secureUrl}
        terms={terms}
        termsError={termsError}
        enhancements={enhancements}
        enhancementsLoading={!!secureUrl && enhancementsPending}
        interaction={interaction}
        interactions={interactionsData?.interactions ?? []}
        organization={organization}
        profileSlug={campaign.profileSlug}
        campaignId={String(programId)}
        onTermsAgreed={handleTermsAgreed}
        onAcceptBasicTerms={handleAcceptBasicTerms}
        onAcceptStandardTerms={handleAcceptStandardTerms}
        isAccepting={
          agreeToBasicMutation.isPending || agreeToStandardMutation.isPending
        }
        userSession={sessionData}
        onProposeChanges={handleProposeChanges}
        isProposingChanges={proposeChangesMutation.isPending}
        isCanceled={isCanceled}
        profileId={profile.id}
        youtubeChannelConnected={youtubeChannelConnected}
        youtubeStudioUrl={youtubeStudioUrl}
        youtubeChannelName={youtubeChannelName}
      />
      {updatedLabel && (
        <p className="text-xs text-muted-foreground text-center py-3 px-6 border-t">
          Last updated {updatedLabel}
        </p>
      )}
    </div>
  );
}
